import { useEffect, createContext, type PropsWithChildren, useCallback, useState } from 'react';

import axios from 'axios';
import { useContainer } from 'unstated-next';

import AdminAuthContainer from './AdminAuthContainer';
import { GET_USER_DATA } from '../../config/apiPaths';
import { getUrls, processApiPath } from '../../utils/url';

export interface DataProviderProps {
    dataId: string | null;
    dataUuid: string;
    onBack?: () => void;
    narrowStyle?: boolean;
    fullScreen?: boolean;
}

interface DataContextInterface {
    dataId: string | null;
    loading: boolean;
    isNarrowStyle: boolean;
    isFullScreen: boolean;
    errorMessage: string;
    errorModalMessage: string;
    setErrorModalMessage: (message: string) => void;
    openErrorModal: boolean;
    setOpenErrorModal: (openEditModal: boolean) => void;
    dataData: any;
    setDataData: (dataData: any) => void;
    onBack?: () => void;
    me: any;
    meFetched: boolean;
}

const DataContext = createContext(undefined as unknown as DataContextInterface);

const { Provider, Consumer } = DataContext;

const DataProvider = (props: PropsWithChildren<DataProviderProps>) => {
    const search = new URLSearchParams(window.location.search);
    const dataUuid = search.get('data_uuid') || window.localStorage.getItem('data_uuid');

    const { authMode, showDemoContent } = useContainer(AdminAuthContainer);
    const { fullScreen, narrowStyle, dataId, children } = props;
    const [isLoading, setIsLoading] = useState(true);
    const [dataData, setDataData] = useState<any>({ loading: true, dataData: {} });
    const [openErrorModal, setOpenErrorModal] = useState<boolean>(false);
    const [errorModalMessage, setErrorModalMessage] = useState<string>('');
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [me, setMe] = useState<any>({});
    const [meFetched, setMeFetched] = useState<boolean>(false);
    const { api } = getUrls();

    console.log('dataProvider dataId', dataId);

    const fetchDataData = useCallback(async () => {
        try {
            // should get extra data not base data
            const { data } = await axios.get(
                `${api}${GET_USER_DATA}?data_id=${dataId}&data_uuid=${dataUuid}&auth_mode=${authMode}&demo_content=${showDemoContent}`
            );
            setDataData(data);
            setIsLoading(false);
        } catch (err) {
            setErrorMessage(err!.toString());
            setDataData(undefined);
            setIsLoading(false);
        }
    }, [api, dataId, authMode, dataUuid, showDemoContent]);

    useEffect(() => {
        if (!dataUuid || !dataId) {
            return;
        }

        const fetchMe = async () => {
            const { data: responseData } = await axios.get(
                processApiPath(GET_USER_DATA, undefined, {
                    data_uuid: dataUuid,
                    data_id: dataId,
                    auth_mode: authMode
                })
            );
            const getMeResponse = responseData.response_too_large_url
                ? await axios.get(responseData.response_too_large_url)
                : { data: responseData };
            setMe(getMeResponse);
            setMeFetched(true);
            fetchDataData();
        };

        fetchMe();
    }, [api, dataId, fetchDataData, authMode, dataUuid]);

    return (
        <Provider
            value={{
                dataId,
                loading: dataData.loading || isLoading,
                isFullScreen: !!fullScreen,
                isNarrowStyle: !!narrowStyle,
                openErrorModal,
                setOpenErrorModal,
                errorModalMessage,
                setErrorModalMessage,
                dataData,
                setDataData,
                me,
                meFetched,
                errorMessage
            }}
        >
            {children}
        </Provider>
    );
};

export { DataContext, DataProvider, Consumer as DataConsumer };
